import type { Chapter, Ledger, StoryBible, StoryMode } from '../types';
import {
  analyzePhysicalArchivistResponseShape,
  normalizeArchivistPhysicalResponse,
  type PhysicalArchivistResponseShape,
  type PhysicalContinuityExtractionValidation,
} from './physicalContinuityExtraction.service';
import { SYSTEM_INSTRUCTIONS } from '../constants';

export type PhysicalCorrectionTransport = (
  request: Record<string, unknown>,
) => Promise<{ text?: string }>;

export interface PhysicalArchivistCorrectionParams {
  chapter: Chapter;
  mode: StoryMode;
  bible?: StoryBible;
  validation: PhysicalContinuityExtractionValidation;
  rawData: Record<string, unknown>;
  existingLedger?: Ledger;
}

function extractJson(text: string): Record<string, unknown> {
  try {
    const trimmed = text.trim();
    const start = trimmed.indexOf('{');
    const end = trimmed.lastIndexOf('}');
    if (start >= 0 && end > start) {
      return JSON.parse(trimmed.slice(start, end + 1)) as Record<string, unknown>;
    }
    return JSON.parse(trimmed) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function buildPhysicalArchivistCorrectionRequest(
  params: PhysicalArchivistCorrectionParams,
): Record<string, unknown> {
  const chapterNumber = typeof params.chapter.id === 'number' ? params.chapter.id : Number(params.chapter.chapterNumber) || 1;
  const correctionInstruction =
    SYSTEM_INSTRUCTIONS.ARCHIVIST +
    `\nPHYSICAL-ONLY CORRECTION PASS (bounded):
Return ONLY continuity_objects and physical_continuity_obligations.
Each continuity object MUST include source_candidate_id from the correction payload.
Do NOT rewrite unrelated characters, plot points, relationships, inventory, or world rules.
Return exactly one continuity object per missing required candidate ID.
Fill every missing required physical object, incomplete field, and open obligation indicated in the correction payload.`;

  const payload = {
    chapterNumber,
    missingObjectCandidateIds: params.validation.missingRequiredObjects.map((candidate) => candidate.candidateId),
    incompleteFields: params.validation.incompleteObjectStates.map((issue) => issue.field),
    missingObligationRequirements: params.validation.missingRequiredObligations.map((issue) => issue.code),
    entityResolutionWarnings: params.validation.incompleteObjectStates
      .filter((issue) => issue.code === 'ENTITY_RESOLUTION_AMBIGUOUS')
      .map((issue) => issue.objectId),
    currentPhysicalOutput: {
      continuity_objects: params.rawData.continuity_objects || [],
      physical_continuity_obligations: params.rawData.physical_continuity_obligations || [],
    },
    extractionSchema: {
      continuity_objects: [
        'source_candidate_id',
        'objectId',
        'canonicalName',
        'objectType',
        'ownerCharacterIds',
        'currentHolderCharacterId',
        'currentLocationDescription',
        'operationalStatus',
        'mobilityStatus',
        'continuityImportance',
        'sourceChapterId',
      ],
      physical_continuity_obligations: [
        'obligationId',
        'objectId',
        'originatingChapter',
        'status',
        'obligation',
        'description',
      ],
    },
  };

  return {
    chapterNumber,
    mode: params.mode,
    correctionInstruction,
    payload,
    contents: `CHAPTER NUMBER: ${chapterNumber}\nMODE: ${params.mode}\nPHYSICAL CORRECTION PAYLOAD:\n${JSON.stringify(payload)}`,
  };
}

export function logPhysicalCorrectionResponseShape(
  correction: Record<string, unknown>,
  chapterNumber: number,
  existingLedger?: Ledger,
  projectId?: string,
): PhysicalArchivistResponseShape {
  const shape = analyzePhysicalArchivistResponseShape(correction, chapterNumber, {
    projectId,
    existingObjects: existingLedger?.continuity_objects,
  });
  console.log(`[PHYSICAL CORRECTION RESPONSE SHAPE] ${JSON.stringify(shape)}`);
  return shape;
}

function readCorrectionArrays(correction: Record<string, unknown>, rawData: Record<string, unknown>) {
  const continuity_objects = Array.isArray(correction.continuity_objects)
    ? [...(correction.continuity_objects as unknown[])]
    : Array.isArray(correction.continuityObjects)
      ? [...(correction.continuityObjects as unknown[])]
      : Array.isArray(rawData.continuity_objects)
        ? [...(rawData.continuity_objects as unknown[])]
        : [];

  const physical_continuity_obligations = Array.isArray(correction.physical_continuity_obligations)
    ? [...(correction.physical_continuity_obligations as unknown[])]
    : Array.isArray(correction.physicalContinuityObligations)
      ? [...(correction.physicalContinuityObligations as unknown[])]
      : Array.isArray(rawData.physical_continuity_obligations)
        ? [...(rawData.physical_continuity_obligations as unknown[])]
        : [];

  return { continuity_objects, physical_continuity_obligations };
}

export async function runPhysicalArchivistCorrection(
  params: PhysicalArchivistCorrectionParams,
  transport: PhysicalCorrectionTransport,
): Promise<Record<string, unknown>> {
  const chapterNumber = typeof params.chapter.id === 'number' ? params.chapter.id : Number(params.chapter.chapterNumber) || 1;
  const request = buildPhysicalArchivistCorrectionRequest(params);
  const response = await transport(request);
  const correction = extractJson(response.text || '{}');
  logPhysicalCorrectionResponseShape(
    correction,
    chapterNumber,
    params.existingLedger,
    params.bible?.canonId,
  );
  return mergePhysicalCorrectionResult(
    params.rawData,
    correction,
    params.validation,
    chapterNumber,
    params.bible,
    params.existingLedger,
  );
}

export function mergePhysicalCorrectionResult(
  rawData: Record<string, unknown>,
  correction: Record<string, unknown>,
  validation: PhysicalContinuityExtractionValidation,
  chapterNumber: number,
  bible?: StoryBible,
  existingLedger?: Ledger,
): Record<string, unknown> {
  const normalizedCorrection = normalizeArchivistPhysicalResponse(correction);
  const { continuity_objects, physical_continuity_obligations } = readCorrectionArrays(
    normalizedCorrection,
    rawData,
  );

  if (!continuity_objects.length && !physical_continuity_obligations.length) {
    return {
      ...rawData,
      continuity_objects: Array.isArray(rawData.continuity_objects) ? rawData.continuity_objects : [],
      physical_continuity_obligations: Array.isArray(rawData.physical_continuity_obligations)
        ? rawData.physical_continuity_obligations
        : [],
    };
  }

  const existingIds = new Set(
    (existingLedger?.continuity_objects || []).map((object) => object.objectId),
  );
  const dedupedObjects: unknown[] = [];
  for (const entry of continuity_objects) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const objectId = typeof record.objectId === 'string' ? record.objectId : '';
    if (!objectId) {
      dedupedObjects.push(entry);
      continue;
    }
    const canonical = String(record.canonicalName || '').toLowerCase();
    const targetId = [...existingIds].find(
      (existingId) => canonical.length >= 4 && existingId.toLowerCase().includes(canonical),
    );
    if (targetId && !existingIds.has(objectId)) {
      dedupedObjects.push({ ...record, objectId: targetId });
      continue;
    }
    dedupedObjects.push(entry);
  }

  return {
    ...rawData,
    continuity_objects: dedupedObjects,
    physical_continuity_obligations,
  };
}
